import { useParams, Link, Navigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import SEO from '@/components/SEO';
import { CoverImage } from '@/components/CoverImage';
import { blogPosts, relatedGuides } from './BlogIndexPage';
import { postContent } from './blogContent';
import { readingTime } from '@/lib/readingTime';
import { buildPostSchema } from '@/lib/postSchema';
import NotFoundPage from './NotFoundPage';
import { ArrowLeft, ArrowRight, Clock, ChevronRight } from 'lucide-react';
import '@/styles/article.css';
import '@/styles/landing.css';
import '@/styles/app-states.css';

// Heading anchors and the table of contents.
//
// The design puts a sticky "On this page" rail beside the article on desktop
// and collapses it to a bordered block above the article on mobile. That needs
// an id on every h2, which the markdown does not carry, so the id is derived
// from the heading TEXT — deterministically, so a given heading always gets
// the same anchor and existing deep links do not move.
//
// This adds an `id` attribute and a nav element. It adds NO copy, changes no
// heading text and changes no heading level.
const slugifyHeading = (text) =>
  String(text)
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .slice(0, 60);

/** The h2 headings of a post, in document order, as {id, text}. */
const tableOfContents = (content) => {
  if (content?.markdownContent) {
    return (content.markdownContent.match(/^##[^#].*$/gm) || [])
      .map((line) => line.replace(/^##\s*/, '').trim())
      .filter(Boolean)
      .map((text) => ({ id: slugifyHeading(text), text }));
  }
  return (content?.sections || [])
    .map((section) => section.heading)
    .filter(Boolean)
    .map((text) => ({ id: slugifyHeading(text), text }));
};

/** Flatten a ReactMarkdown children array back to plain text for the anchor. */
const headingText = (children) =>
  (Array.isArray(children) ? children : [children])
    .map((c) => (typeof c === 'string' ? c : c?.props?.children ?? ''))
    .join('');

// ReactMarkdown renderers for the article body.
//
// The scale, the rhythm and every block treatment live in styles/article.css
// under `.uxa-article`, so these components mostly hand the element straight
// through and let the stylesheet set it. That is deliberate: the previous
// version carried 20 inline style objects, which meant the article scale
// existed in a JS file and could not be checked, overridden or measured.
//
// THIS IS A PRESENTATION CHANGE ONLY. Heading LEVELS are handed through
// unchanged — an h2 in the markdown is an h2 in the DOM. The blog is indexed
// and its heading hierarchy is part of what is indexed.
const mdComponents = {
  h2: ({ children, ...rest }) => <h2 id={slugifyHeading(headingText(children))} {...rest}>{children}</h2>,
  h3: ({ children, ...rest }) => <h3 {...rest}>{children}</h3>,
  h4: ({ children, ...rest }) => <h4 {...rest}>{children}</h4>,
  p: ({ children }) => <p>{children}</p>,
  strong: ({ children }) => <strong>{children}</strong>,
  em: ({ children }) => <em>{children}</em>,
  ul: ({ children }) => <ul>{children}</ul>,
  ol: ({ children }) => <ol>{children}</ol>,
  li: ({ children }) => <li>{children}</li>,
  hr: () => <hr />,
  code: ({ children }) => <code>{children}</code>,
  blockquote: ({ children }) => <blockquote>{children}</blockquote>,
  // 16px cells, header rule in ink, and horizontal scroll on mobile rather
  // than a shrink below the readable floor.
  table: ({ children }) => (
    <div className="uxa-table-scroll">
      <table>{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead>{children}</thead>,
  th: ({ children }) => <th>{children}</th>,
  td: ({ children }) => <td>{children}</td>,
  a: ({ href, children }) => {
    // Internal static files (e.g. /ux-audit-template.html) must be real anchors,
    // not React Router links, so the browser fetches the file directly.
    const isInternalFile = href && href.startsWith('/') && /\.[a-z0-9]+$/i.test(href.split('?')[0]);
    if (href && href.startsWith('/') && !isInternalFile) {
      return <Link to={href}>{children}</Link>;
    }
    const external = !href || !href.startsWith('/');
    return (
      <a href={href} {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
        {children}
      </a>
    );
  },
  // Markdown images render as a figure. The title (third arg of ![alt](src "title"))
  // becomes a caption. alt is always present; the build check enforces it.
  img: ({ src, alt, title }) => (
    <figure>
      <img src={src} alt={alt || ''} loading="lazy" />
      {title && <figcaption>{title}</figcaption>}
    </figure>
  ),
};

// Merged posts (Phase 2 site architecture). The losing slug 301s to its survivor at
// the server (see backend/server.py and frontend nginx). This client map keeps in-app
// navigation consistent for users who click an old link inside the SPA.
const MERGED_REDIRECTS = {
  'how-to-ux-audit': 'how-to-do-a-ux-audit',
  'ux-audit-checklist-47': 'ux-audit-checklist',
  'ux-audit-report-template': 'ux-audit-template',
  'best-ux-audit-ai-tools-2026': 'best-ux-audit-tools',
};

export default function BlogPostPage() {
  const { slug } = useParams();

  const mergedInto = MERGED_REDIRECTS[slug];
  if (mergedInto) return <Navigate to={`/blog/${mergedInto}/`} replace />;

  const post = blogPosts.find((p) => p.slug === slug);

  // Unknown slug: render the 404 view (noindex, no canonical), never redirect to the
  // index. The server returns a real 404 status for these paths (see nginx.conf).
  if (!post) return <NotFoundPage />;

  const content = postContent[slug];

  const coverUrl = post.cover ? `https://www.uxauditpro.com${post.cover}` : undefined;
  const readTime = readingTime(content);
  const schemas = buildPostSchema(post, content);
  const related = relatedGuides(post.slug, 3);

  const toc = tableOfContents(content);

  return (
    <div style={{ minHeight: '100vh' }}>
      <SEO
        /* NO " | UXAuditPro Blog" SUFFIX, AND `seoTitle` RATHER THAN `title`.
           The suffix cost 18 characters on every post and put 20 of 22 titles
           past the ~60 Google renders; dropping it alone brought 19 of them back
           under, which is why it is removed rather than shortened.

           `post.title` is NOT a SERP-only string: BlogPostPage renders it as the
           H1, the breadcrumb leaf and the cover image alt. Retitling for search
           by editing it would silently reword nine H1s, which the content
           preservation rule in CLAUDE.md forbids. So `seoTitle` is a separate,
           optional field read ONLY here, and it is present on the nine posts
           whose SERP title had to differ (an em dash, or still over 60 without
           the suffix). Everything else falls through to `title` and the two
           stay in sync by default. */
        fullTitle={post.seoTitle || post.title}
        description={post.metaDescription || post.excerpt}
        canonical={`/blog/${post.slug}`}
        ogType="article"
        {...(coverUrl ? { ogImage: coverUrl } : {})}
        structuredData={schemas}
      />

      {/* Header */}
      {/* MINIMAL CHANGE, DELIBERATELY. This page was already on the system: the
          article scale, .uxa-upgrade for the inline CTA, --radius-card on the
          related-guide cards (pinned at count 1 in
          styles/__tests__/surfaces-radius-glow.test.js) and --target-min on the
          back link. Converting its cards would have dropped that count to 0 and
          reclassified a surface for no reason other than uniformity.
          The grid wash behind the header is the whole change. */}
      <header className="uxa-pt-xl uxa-pb-md px-6 hero" style={{ position: 'relative' }}>
        <div className="uxa-hero-grid" aria-hidden="true" />
        <div className="mx-auto" style={{ maxWidth: '880px', position: 'relative' }}>
          {/* Breadcrumb: Home > Blog > Post */}
          <nav aria-label="Breadcrumb" className="mb-6">
            <ol className="flex items-center gap-2 uxa-meta" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {/* 44px MINIMUM. Measured 31x15 each on the built page at all
                  four widths. A breadcrumb sits in an <li>, which is why the
                  first version of my sweep missed it — the WCAG 2.5.8
                  inline-prose exemption covers a link inside a SENTENCE, and a
                  breadcrumb is navigation that happens to use list markup. The
                  sweep now excludes <nav> from that exemption. */}
              <li><Link to="/" style={{ color: 'var(--ink-2)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minHeight: 44, minWidth: 44 }}>Home</Link></li>
              <li aria-hidden="true"><ChevronRight className="w-3.5 h-3.5" /></li>
              <li><Link to="/blog/" style={{ color: 'var(--ink-2)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minHeight: 44, minWidth: 44 }}>Blog</Link></li>
              <li aria-hidden="true"><ChevronRight className="w-3.5 h-3.5" /></li>
              <li aria-current="page" className="truncate" style={{ color: 'var(--ink-1)', fontWeight: 700, maxWidth: '55vw' }}>{post.title}</li>
            </ol>
          </nav>
          <Link
            to="/blog/"
            className="inline-flex items-center gap-2 mb-8"
            style={{ minHeight: 'var(--target-min)', fontSize: 'var(--type-body-size)', fontWeight: 500, color: 'var(--ink-2)' }}
          >
            <ArrowLeft className="w-4 h-4" aria-hidden="true" /> Back to Blog
          </Link>

          {/* Category, date and read time as one middot-separated meta line —
              the design's own treatment for article metadata. */}
          <div className="flex items-center flex-wrap gap-3 mb-5 uxa-meta" style={{ letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            <span style={{ color: 'var(--link)' }}>{post.category}</span>
            <span aria-hidden="true">&middot;</span>
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" aria-hidden="true" /> {readTime}</span>
          </div>

          <h1
            className="uxa-h1 mb-5"
            style={{ color: 'var(--ink-1)',
              maxWidth: '22ch',
              textWrap: 'pretty' }}
          >
            {post.title}
          </h1>
          {/* ELEVEN OF TWENTY POSTS HAVE NO `intro`, so the standfirst is absent
              on more than half the blog. The rule that closes the header used to
              hang off the standfirst, and the no-intro branch rendered a bare
              bordered div with 0.5rem of margin — a hairline under the headline
              with nothing above it, which reads as a broken element rather than
              as a deliberate absence.

              The rule now belongs to the HEADER, not to the standfirst, so it
              sits at the same place with or without one and the layout collapses
              cleanly. This matches the design, where the hairline is the
              border-top of the byline row rather than an underline on the
              standfirst.

              Writing the eleven missing standfirsts is a content job, not a
              template one. */}
          {content?.intro && <p className="uxa-standfirst">{content.intro}</p>}
          <div
            style={{
              borderBottom: '1px solid var(--border-2)',
              marginTop: content?.intro ? '2rem' : '1.5rem',
            }}
          />
        </div>
      </header>

      {/* Cover image */}
      {post.cover && (
        <section className="uxa-pb-xs px-6">
          <div className="mx-auto" style={{ maxWidth: '880px' }}>
            {/* priority: this sits directly under the h1 and is almost
                always the LCP element, so it takes fetchpriority=high as well
                as eager. */}
            <CoverImage
              src={post.cover}
              alt={post.title}
              priority
              className="w-full"
              style={{ border: '1px solid var(--border-2)', borderRadius: 'var(--radius-md)' }}
            />
          </div>
        </section>
      )}

      {/* Content */}
      <section className="uxa-pb-lg px-6 uxa-pt-md">
        <div className="uxa-article-shell mx-auto" style={{ maxWidth: '1180px' }}>
          {toc.length > 1 ? (
            <nav aria-label="Table of contents" className="uxa-toc">
              <p className="uxa-toc-title">On this page</p>
              <div className="uxa-toc-list">
                {toc.map((h) => (
                  <a key={h.id} href={`#${h.id}`}>{h.text}</a>
                ))}
              </div>
            </nav>
          ) : (
            <div aria-hidden="true" />
          )}

          <article className="uxa-article">
            {content?.markdownContent ? (
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                {content.markdownContent}
              </ReactMarkdown>
            ) : (
              <>
                {content?.sections?.map((section) => (
                  <div key={section.heading}>
                    <h2 id={slugifyHeading(section.heading)}>{section.heading}</h2>
                    {section.body.split('\n').map((para, i) => (
                      <p key={i}>{para}</p>
                    ))}
                  </div>
                ))}
              </>
            )}

            {/* FAQ block (also emitted as FAQPage schema via buildPostSchema) */}
            {content?.faq?.length > 0 && (
              <div style={{ marginTop: 'var(--article-h2-top)' }}>
                <h2 id="frequently-asked-questions">Frequently asked questions</h2>
                <div className="space-y-3" style={{ marginTop: 'var(--space-6)' }}>
                  {content.faq.map((f, i) => (
                    <details
                      key={i}
                      style={{ backgroundColor: 'var(--surface-white)', border: '1px solid var(--border-2)', borderRadius: 'var(--radius-field)', padding: '18px 20px' }}
                    >
                      <summary style={{ fontSize: 'var(--type-body-size)', fontWeight: 700, color: 'var(--ink-1)', cursor: 'pointer', minHeight: 'var(--target-min)', display: 'flex', alignItems: 'center' }}>
                        {f.q}
                      </summary>
                      {/* --article-size, not --type-body-size. This is article
                          prose inside .uxa-article; it was rendering at the UI
                          body size (16px) while the post around it ran at 19px.
                          The <details> nesting is why `.uxa-article > p` never
                          reached it. */}
                      <p style={{ fontSize: 'var(--article-size)', lineHeight: 'var(--article-leading)', color: 'var(--ink-2)', marginTop: '12px', marginBottom: 0 }}>
                        {f.a}
                      </p>
                    </details>
                  ))}
                </div>
              </div>
            )}
          </article>

          <div aria-hidden="true" />
        </div>
      </section>

      {/* CTA inline */}
      <section className="uxa-pt-md uxa-pb-md px-6">
        <div className="mx-auto" style={{ maxWidth: '880px' }}>
          <div className="uxa-upgrade">
            <p className="uxa-upgrade-eyebrow">Try it yourself</p>
            <h3 className="uxa-upgrade-title">
              Run an AI UX audit in under 5 minutes
            </h3>
            <p style={{ fontSize: '16px', lineHeight: 1.6, color: 'var(--ink-4)', margin: '12px 0 24px', maxWidth: '520px' }}>
              One free audit every month. No credit card required. Get a prioritized report with sprint roadmap.
            </p>
            <Link to="/ux-auditor/" className="uxa-upgrade-cta" style={{ width: 'auto', display: 'inline-flex' }}>
              Try UXAuditPro Free
              <span className="uxa-cta-knob" style={{ width: '36px', height: '36px' }} aria-hidden="true"><ArrowRight className="w-4 h-4" /></span>
            </Link>
          </div>
        </div>
      </section>

      {/* Related guides (chosen by cluster, not by date) */}
      <section className="uxa-pb-xl px-6">
        <div className="mx-auto" style={{ maxWidth: '880px' }}>
          <p className="uxa-eyebrow mb-4">Related guides</p>
          <div className="grid grid-cols-1 gap-4">
            {related.map((rp) => (
              <Link key={rp.slug} to={`/blog/${rp.slug}/`} className="block group">
                <div className="p-6" style={{ backgroundColor: 'var(--surface-white)', border: '1px solid var(--border-1)', borderRadius: 'var(--radius-card)' }}>
                  <div className="flex items-center gap-3 mb-2">
                    <span className="uxa-meta" style={{ letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--link)' }}>{rp.category}</span>
                  </div>
                  <h3 className="uxa-h3" style={{ color: 'var(--ink-1)', marginBottom: '8px' }}>
                    {rp.title}
                  </h3>
                  <p style={{ fontSize: '16px', lineHeight: 1.6, color: 'var(--ink-2)', marginBottom: '16px' }}>{rp.excerpt}</p>
                  <span
                    className="inline-flex items-center gap-2"
                    style={{ fontSize: 'var(--type-body-size)', fontWeight: 700, color: 'var(--link)', textDecoration: 'underline', textUnderlineOffset: '3px' }}
                  >
                    Read article <ArrowRight className="w-4 h-4" aria-hidden="true" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
